import { z } from "zod";

// Enum for the broad categorization of the error
// Source: Properties -> error_type
export const PlaidErrorTypeSchema = z.enum([
  "INVALID_REQUEST",
  "INVALID_RESULT",
  "INVALID_INPUT",
  "INSTITUTION_ERROR",
  "RATE_LIMIT_EXCEEDED",
  "API_ERROR",
  "ITEM_ERROR",
  "ASSET_REPORT_ERROR",
  "RECAPTCHA_ERROR",
  "OAUTH_ERROR",
  "PAYMENT_ERROR",
  "BANK_TRANSFER_ERROR",
  "INCOME_VERIFICATION_ERROR",
  "MICRODEPOSITS_ERROR",
  "SANDBOX_ERROR",
  "PARTNER_ERROR",
  "SIGNAL_ERROR",
  "TRANSACTIONS_ERROR",
  "TRANSACTION_ERROR",
  "TRANSFER_ERROR",
  "CHECK_REPORT_ERROR",
  "CONSUMER_REPORT_ERROR",
  "USER_ERROR",
]);

// Enum for specific reasons, currently only used for OAuth-based item errors
// Source: Properties -> error_code_reason
export const PlaidErrorCodeReasonSchema = z.enum([
  "OAUTH_INVALID_TOKEN",
  "OAUTH_CONSENT_EXPIRED",
  "OAUTH_USER_REVOKED",
]);

// Main Plaid Error Schema
// We use z.lazy() to handle the recursive 'causes' field which contains nested errors
export const PlaidErrorSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    // A broad categorization of the error
    error_type: PlaidErrorTypeSchema,

    // The particular error code (e.g., 'NO_ACCOUNTS')
    error_code: z.string(),

    // A developer-friendly representation of the error code
    error_message: z.string(),

    // A user-friendly representation of the error code.
    // Null if the error is not related to user action.
    display_message: z.string().optional().nullable(),

    // A unique ID identifying the request for troubleshooting
    request_id: z.string().optional().nullable(), // Webhooks may omit this

    // Specific reason for the error code (mostly null)
    error_code_reason: PlaidErrorCodeReasonSchema.optional().nullable(),

    // HTTP status code (only returned in webhooks)
    status: z.number().int().optional().nullable(),

    // URL to Plaid documentation about the error
    documentation_url: z.string().optional().nullable(),

    // Suggested steps for resolving the error
    suggested_action: z.string().optional().nullable(),

    // Nested errors (breakdown on individual Item level)
    causes: z.array(PlaidErrorSchema).optional().nullable(),

    // Specific to NO_ACCOUNTS errors with investments_auth
    required_account_subtypes: z.array(z.string()).optional().nullable(),
    provided_account_subtypes: z.array(z.string()).optional().nullable(),
  }),
);

export type PlaidError = z.infer<typeof PlaidErrorSchema>;
